// src/pages/LawyerDetail.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import "./lawyerDetail.css";
import RatingIcon from "../searchResults/assets/rating.png";
import PracticeIcon from "../searchResults/assets/practice.png";
import LocationIcon from "../searchResults/assets/location.png";
import FirmIcon from "../searchResults/assets/firm.png";
import ConsultationBox from "../lawyerSelection/components/consultationBox";
import AvailabilityCalendar from "../lawyerSelection/components/availabilityCalendar";
import AppointmentModal from "./components/appointmentModal";
import StarEmpty from "./assets/starEmpty.png";
import StarFill from "./assets/starFill.png";
import LeftEnabled from "./assets/left-enabled.png";
import LeftDisabled from "./assets/left-disabled.png";
import RightEnabled from "./assets/right-enabled.png";
import RightDisabled from "./assets/right-disabled.png";
import { parseLocation } from "../../utils";
import SearchHeader from "../searchResults/searchHeader";
import ClientLoginPopup from "../../home/new/components/clientLoginPopup";
import PracticeAreas from "./components/practiceAreas";
import ExperienceSection from "./components/experienceSection";
import EducationSection from "./components/educationSection";
import Footer from "../../components/footer";
import CaseHighlights from "./components/caseHighlights";
import ReviewsSection from "../components/ReviewsSection";
import moment from "moment";
import { getPracticeAreaLabel } from "../../helpers/stringHelper";

const AboutComponent = ({ lawyer }) => {
  return (
    <div className="about-component">
      <h2 className="about-title">About {lawyer.name}</h2>

      <p className="about-paragraph">
        Throughout their professional career, {lawyer.name} has been a dedicated
        advocate for individuals' rights, specializing in{" "}
        {lawyer.practice_areas[0]}. Their unwavering commitment to justice and
        equality drives them to tirelessly represent clients, ensuring that
        every voice is heard and rights are upheld. With expertise in both
        areas, {lawyer.name} has made significant contributions to fostering
        positive change and promoting fairness within the legal system.
      </p>

      <h2 className="about-title">Credentials</h2>
      <div
        style={{
          paddingBottom: "30px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <span className="credentials-span">
          - Admitted to practice law in{" "}
          {lawyer.bar_registrations[0].stateOfRegistration}
        </span>
        <span className="credentials-span">
          - Has {lawyer.years_of_experience} years of experience
        </span>
      </div>
    </div>
  );
};

const LawyerDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Refs for scrolling to sections
  const aboutRef = useRef(null);
  const reviewsRef = useRef(null);
  const experienceRef = useRef(null);
  const activityRef = useRef(null);

  // State for lawyer details
  const [lawyer, setLawyer] = useState(location.state?.lawyer || null);
  const [loadingLawyer, setLoadingLawyer] = useState(!lawyer);
  const [errorLawyer, setErrorLawyer] = useState(null);

  // State for consultations
  const [consultations, setConsultations] = useState([]);
  const [loadingConsultations, setLoadingConsultations] = useState(true);
  const [errorConsultations, setErrorConsultations] = useState(null);

  // State for availability
  const [availability, setAvailability] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(true);
  const [errorAvailability, setErrorAvailability] = useState(null);
  const [availabilityPage, setAvailabilityPage] = useState(1);
  const [startDate, setStartDate] = useState(new Date()); // Today
  const [endDate, setEndDate] = useState(
    new Date(new Date().setDate(new Date().getDate() + 13))
  ); // 14 days from today
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedConsultationId, setSelectedConsultationId] = useState(null);

  // Scroll functions for tabs
  const scrollToAbout = () => {
    setActiveSection('about');
    setTimeout(() => {
      aboutRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const scrollToReviews = () => {
    setActiveSection('reviews');
    setTimeout(() => {
      reviewsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const scrollToExperience = () => {
    setActiveSection('experience');
    setTimeout(() => {
      experienceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const scrollToActivity = () => {
    setActiveSection('activity');
    setTimeout(() => {
      activityRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  useEffect(() => {
    if (!lawyer && id) {
      // Only fetch if lawyer is null and id is valid
      const fetchLawyer = async () => {
        setLoadingLawyer(true);
        setErrorLawyer(null);
        try {
          const response = await fetch(
            `/api/attorney/fetchAttorneyData?userId=${id}`
          );
          if (!response.ok) {
            throw new Error(`Error: ${response.status}`);
          }
          const data = await response.json();
          setLawyer(data); // Update lawyer state
        } catch (err) {
          console.error("Failed to fetch lawyer details:", err);
          setErrorLawyer("Failed to load lawyer details.");
        } finally {
          setLoadingLawyer(false);
        }
      };
      fetchLawyer();
    }
  }, [id, lawyer]); // Trigger when `id` or `lawyer` changes

  // Log changes to showAppointmentModal
  useEffect(() => {
    console.log("showAppointmentModal changed to:", showAppointmentModal);
  }, [showAppointmentModal]);

  // Log changes to selectedConsultationId
  useEffect(() => {
    console.log("selectedConsultationId changed to:", selectedConsultationId);
  }, [selectedConsultationId]);

  // Track which section is currently active
  const [activeSection, setActiveSection] = useState('about');

  // Add scroll listener to update active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100; // Offset for header

      // Get the position of each section
      const aboutPosition = aboutRef.current?.offsetTop || 0;
      const reviewsPosition = reviewsRef.current?.offsetTop || 0;
      const experiencePosition = experienceRef.current?.offsetTop || 0;
      const activityPosition = activityRef.current?.offsetTop || 0;

      // Determine which section is currently in view
      if (scrollPosition >= activityPosition) {
        setActiveSection('activity');
      } else if (scrollPosition >= experiencePosition) {
        setActiveSection('experience');
      } else if (scrollPosition >= reviewsPosition) {
        setActiveSection('reviews');
      } else if (scrollPosition >= aboutPosition) {
        setActiveSection('about');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);



  const handleDayClick = (day) => {
    // If no consultation is selected, show an alert
    if (!selectedConsultationId) {
      alert("Please select a consultation type first.");
      return;
    }

    // Otherwise, set the chosen day
    setSelectedDay(day);

    // Open your booking popup
    setShowAppointmentModal(true);

    // Optional: Log to verify
  };

  const getPracticeAreaColor = (areaLabel) => {
    const colorMap = {
      red: [
        "Criminal Law",
        "White Collar Crime",
        "Drug Offenses",
        "DUI/DWI",
        "Violent Crimes",
        "Juvenile Delinquency",
        "Federal Crimes",
        "Sex Crimes",
      ],
      blue: [
        "Corporate Law",
        "Mergers & Acquisitions",
        "Corporate Governance",
        "Securities Law",
        "Commercial Contracts",
        "Regulatory Compliance",
        "Corporate Litigation",
      ],
      green: [
        "Family Law",
        "Divorce",
        "Child Custody & Visitation",
        "Child Support",
        "Adoption",
        "Prenuptial & Postnuptial Agreements",
        "Domestic Violence",
        "Guardianship & Conservatorship",
      ],
      orange: [
        "Personal Injury Law",
        "Motor Vehicle Accidents",
        "Slip and Fall",
        "Product Liability",
        "Workplace Accidents",
        "Premises Liability",
        "Catastrophic & Brain Injuries",
      ],
      purple: [
        "Real Estate Law",
        "Residential Transactions",
        "Commercial Transactions",
        "Landlord-Tenant Disputes",
        "Zoning & Land Use",
        "Property Development & Planning",
        "Title, Escrow & Foreclosure",
      ],
      gray: [], // fallback
    };

    for (const [color, labels] of Object.entries(colorMap)) {
      if (labels.includes(areaLabel)) {
        return `practice-area-${color}`;
      }
    }

    return "practice-area-gray";
  };

  // Helper functions
  const formatDate = (date) => {
    const options = { month: "short", day: "numeric" };
    return date.toLocaleDateString(undefined, options);
  };

  const getWeekday = (date) => {
    const options = { weekday: "short" };
    return date.toLocaleDateString(undefined, options);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Construct the display string
  const displayStartDate = isToday(startDate)
    ? `Today, ${formatDate(startDate)}`
    : formatDate(startDate);
  const displayEndDate = `${getWeekday(endDate)}, ${formatDate(endDate)}`;

  // Fetch Consultations
  useEffect(() => {
    const fetchConsultations = async () => {
      setLoadingConsultations(true);
      setErrorConsultations(null);
      try {
        const response = await fetch(
          `/api/attorney/webapp/fetchAppointmentTypes?user_id=${id}`
        );
        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }

        const data = await response.json();

        setConsultations(data.appointmentTypes || []); // Ensure it's an array
      } catch (err) {
        console.error("Failed to fetch consultations:", err);
        setErrorConsultations("Failed to load consultations.");
      } finally {
        setLoadingConsultations(false);
      }
    };
    fetchConsultations();
  }, [id]);

  // Fetch Availability
  const fetchAvailability = useCallback(async () => {
    setLoadingAvailability(true);
    setErrorAvailability(null);
    try {
      const formattedStartDate = startDate.toISOString().split("T")[0];
      const formattedEndDate = endDate.toISOString().split("T")[0];
      console.log("API Parameters:", formattedStartDate, formattedEndDate);
      const response = await fetch(
        `/api/attorney/availability?lawyer_id=${id}&start_date=${formattedStartDate}&end_date=${formattedEndDate}`
      );
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      const data = await response.json();
      setAvailability(data || []); // Ensure it's an array
    } catch (err) {
      console.error("Failed to fetch availability:", err);
      setErrorAvailability("Failed to load availability.");
    } finally {
      setLoadingAvailability(false);
    }
  }, [id, startDate, endDate]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  // Handle Next Page for Availability
  const handleNextAvailability = () => {
    const newStartDate = new Date(endDate);
    newStartDate.setDate(newStartDate.getDate() + 1);
    const newEndDate = new Date(newStartDate);
    newEndDate.setDate(newStartDate.getDate() + 13); // Next 14 days
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setAvailabilityPage((prev) => prev + 1);
  };

  // Handle Previous Page for Availability
  const handlePrevAvailability = () => {
    if (availabilityPage === 1) return; // Prevent going to pages before 1
    const newEndDate = new Date(startDate);
    newEndDate.setDate(newEndDate.getDate() - 1);
    const newStartDate = new Date(newEndDate);
    newStartDate.setDate(newEndDate.getDate() - 13);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setAvailabilityPage((prev) => prev - 1);
  };

  // Handle Consultation Selection (Single Selection)
  const handleConsultationSelect = (consultationId) => {
    setSelectedConsultationId((prevId) =>
      prevId === consultationId ? null : consultationId
    );
  };
  const [showLoginPopup, setShowLoginPopup] = useState(false);

  const openLoginPopup = () => setShowLoginPopup(true);

  // Close Login Popup function
  const closeLoginPopup = () => setShowLoginPopup(false);

  // Generate Static Map URL using Here Maps
  const getStaticMapUrl = () => {
    if (!lawyer) {
      return "";
    }

    const parsedLocation = parseLocation(lawyer.lat, lawyer.long);
    if (!parsedLocation) {
      console.warn("Lawyer firm location is unavailable.");
      return "";
    }

    const { latitude, longitude } = parsedLocation;
    const apiKey = process.env.REACT_APP_HERE_API_KEY; // Ensure this environment variable is set
    const zoom = 13;
    const size = "580x350"; // Size of the image (width x height)
    const format = "png"; // Image format (png, jpeg, etc.)

    // Construct the GeoJSON for a single marker at the lawyer's location
    const geojson = JSON.stringify({
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          properties: {
            label: lawyer.name,
            color: "%23144A8C", // Red color (#F00)
            size: "medium",
          },
          geometry: {
            type: "Point",
            coordinates: [longitude, latitude], // Coordinates for the marker
          },
        },
      ],
    });

    // Construct the URL using the v3 API endpoint with GeoJSON for the marker
    const mapUrl = `https://image.maps.hereapi.com/mia/v3/base/mc/center:${latitude},${longitude};zoom=${zoom}/${size}/${format}?apiKey=${apiKey}&geojson=${encodeURIComponent(
      geojson
    )}`;
    return mapUrl;
  };

  // Find the selected consultation object
  const selectedConsultation = consultations.find(
    (c) => c.id === selectedConsultationId
  );

  if (!lawyer) {
    return (
      <div className="loading-container">
        <p>Loading lawyer details...</p>
      </div>
    );
  }

  return (
    <div className="lawyer-detail-container">

      <SearchHeader
        openLoginPopup={openLoginPopup}
        headerColor="search-header-gray"
        searchBar={false}
      />

      <div className="blue-header">
        {lawyer.profile_photo_url ? (
          <img
            src={lawyer.profile_photo_url}
            alt={`${lawyer.name}'s profile`}
            className="profile-photo-detail"
            loading="lazy"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/default-avatar.png"; // Path to a default avatar image
            }}
          />
        ) : (
          <div className="profile-photo-detail placeholder">
            {lawyer.name.charAt(0).toUpperCase()}
          </div>
        )}
        <h1 className="lawyer-detail-name">{lawyer.name}</h1>
      </div>

      {/* Lawyer Profile Section */}
      <div className="lawyer-profile">
        <div className="first-row-detail">
          <div className="profile-header">
            <div className="profile-info">
              <div className="info-item-stars">
                <div className="stars">
                  {/* Render Gold Stars */}
                  {[...Array(Math.floor(lawyer.average_rating))].map(
                    (_, index) => (
                      <img
                        key={`gold-${index}`}
                        src={StarFill}
                        alt="Gold Star"
                        className="star-icon"
                      />
                    )
                  )}

                  {/* Render Gray Stars */}
                  {[...Array(5 - Math.floor(lawyer.average_rating))].map(
                    (_, index) => (
                      <img
                        key={`gray-${index}`}
                        src={StarEmpty}
                        alt="Gray Star"
                        className="star-icon"
                      />
                    )
                  )}
                </div>
                <span className="rating">{lawyer.average_rating}</span>
                <span className="reviews">
                  See all {lawyer.number_of_reviews} reviews
                </span>
              </div>
              <div className="info-item">
                <img src={FirmIcon} alt="Firm" className="info-icon" />
                <span
                  onClick={() => {
                    navigate(`/firm-detail/${lawyer.firm_id}`);
                  }}
                  className="lawyer-details-font"
                  style={{ cursor: "pointer", color: "#144A8C" }}
                >
                  {lawyer.firm_name}
                </span>
              </div>
              <div className="info-item">
                <img src={LocationIcon} alt="Location" className="info-icon" />
                <span className="lawyer-details-font">
                  {lawyer.city}, {lawyer.state} {lawyer.zip_code}
                </span>
              </div>
              <div className="info-item">
                <img
                  src={PracticeIcon}
                  alt="Practice Areas"
                  className="info-icon"
                />
                <div className="practice-areas-list">
                  {lawyer.practice_areas.map((area, index) => (
                    <span
                      key={index}
                      className={`practice-area-badge ${getPracticeAreaColor(
                        getPracticeAreaLabel(area)
                      )}`}
                    >
                      {getPracticeAreaLabel(area)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Static Map */}
          <div className="map-section">
            {getStaticMapUrl() ? (
              <img
                src={getStaticMapUrl()}
                alt="Lawyer Location Map"
                className="static-map"
              />
            ) : (
              <div className="map-placeholder">Map is unavailable.</div>
            )}
          </div>

          <div className="office-location-details">
            <span className="office-title">Firm Location</span>
            <span className="office-location-value">
              {lawyer.city}, {lawyer.state} {lawyer.zip_code}
            </span>
            <span className="office-get-directions">Get Directions</span>
          </div>
        </div>

        {/* Consultation Boxes */}
        <div className="consultations-section">
          {loadingConsultations && <div className="spinner"></div>}
          {errorConsultations && (
            <div className="error-message">{errorConsultations}</div>
          )}
          {!loadingConsultations &&
            !errorConsultations &&
            consultations.length === 0 && (
              <div>No consultations available.</div>
            )}
          {!loadingConsultations &&
            !errorConsultations &&
            consultations.length > 0 && (
              <div className="consultations-list">
                {consultations.map((consultation) => (
                  <ConsultationBox
                    key={consultation.id}
                    consultation={consultation}
                    isSelected={selectedConsultationId === consultation.id}
                    onSelect={() => handleConsultationSelect(consultation.id)}
                  />
                ))}
              </div>
            )}
        </div>

        {/* Availability Section */}
        <div className="availability-section">
          {/* Flex Container for Date Range and Pagination */}
          <div className="availability-header">
            {/* Formatted Date Range */}
            <div className="formatted-date-range">
              <span>
                {displayStartDate} - {displayEndDate}
              </span>
            </div>

            {/* Pagination Controls */}
            <div className="availability-pagination">
              {/* Previous Button */}
              <button
                onClick={handlePrevAvailability}
                disabled={availabilityPage === 1 || loadingAvailability}
                aria-label="Previous Page"
                className="pagination-button"
              >
                <img
                  src={
                    availabilityPage === 1 || loadingAvailability
                      ? LeftDisabled
                      : LeftEnabled
                  }
                  alt="Previous Page"
                  className="pagination-icon"
                />
              </button>

              {/* Next Button */}
              <button
                onClick={handleNextAvailability}
                disabled={loadingAvailability}
                aria-label="Next Page"
                className="pagination-button"
              >
                <img
                  src={loadingAvailability ? RightDisabled : RightEnabled}
                  alt="Next Page"
                  className="pagination-icon"
                />
              </button>
            </div>
          </div>

          {/* Loading, Error, and Availability Calendar */}
          {loadingAvailability && <div className="spinner"></div>}
          {errorAvailability && (
            <div className="error-message">{errorAvailability}</div>
          )}
          {!loadingAvailability &&
            !errorAvailability &&
            availability.length === 0 && (
              <div>No availability data available.</div>
            )}
          {!loadingAvailability &&
            !errorAvailability &&
            availability.length > 0 && (
              <AvailabilityCalendar
                availability={availability}
                onDayClick={handleDayClick}
              />
            )}

          {/* "View all Availability" Link */}
          <span
            className="consultation-view-availability"
            onClick={() =>
              handleDayClick({ date: new Date(), day: "Today", slots: [] })
            } // Example for "View all Availability"
          >
            View all Availability
          </span>
        </div>

        {/* Appointment Modal */}
        {showAppointmentModal && selectedConsultation && (
          <AppointmentModal
            lawyer={lawyer}
            consultation={selectedConsultation}
            selectedDay={selectedDay}
            availability={availability}
            onClose={() => setShowAppointmentModal(false)}
          />
        )}
      </div>

      <div className="gray-background">
        <div className="gray-section-inner-container">
                      <div className="view-toggle-buttons">
              <button
                onClick={scrollToAbout}
                className={`view-toggle-button ${activeSection === 'about' ? "active" : ""}`}
              >
                About
              </button>
              <button
                onClick={scrollToReviews}
                className={`view-toggle-button ${activeSection === 'reviews' ? "active" : ""}`}
              >
                Reviews
              </button>
              <button
                onClick={scrollToExperience}
                className={`view-toggle-button ${activeSection === 'experience' ? "active" : ""}`}
              >
                Experience
              </button>
              <button
                onClick={scrollToActivity}
                className={`view-toggle-button ${activeSection === 'activity' ? "active" : ""}`}
              >
                Activity
              </button>
            </div>
          {showLoginPopup && (
            <ClientLoginPopup show={showLoginPopup} onClose={closeLoginPopup} />
          )}
          
          {/* About Section */}
          <div ref={aboutRef}>
            <AboutComponent lawyer={lawyer} />
          </div>
          
          {/* Reviews Section */}
          <div ref={reviewsRef}>
            <ReviewsSection lawyerId={lawyer.user_id} />
          </div>
          
          <hr className="h-rule" />
          <PracticeAreas practiceAreasData={lawyer.specialty} />
          <hr className="h-rule" />

          {/* Experience Section */}
          <div ref={experienceRef}>
            <p className="experience-section-title">Experience</p>
            <ExperienceSection userId={id} />
            <hr className="h-rule" />
            <EducationSection userId={id} />
          </div>
          
          <hr className="h-rule" />
          
          {/* Activity Section */}
          <div ref={activityRef}>
            <CaseHighlights userId={id} />
          </div>
        </div>
      </div>
      <div style={{ width: "100%" }}>
        <Footer />
      </div>
    </div>
  );
};

export default LawyerDetail;
